<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { Menu, SegmentGroup, Switch } from '@ark-ui/vue';
import { useMouseInElement, useElementSize, useElementHover } from '@vueuse/core';
import { useAppStore } from '@/home/stores/app';
import { useFetch } from '@/home/hooks/fetch';
import { useAsyncData } from '@/home/hooks/async-data';
import { gotoLoginForClient, gotoLogoutForClient } from '@/common/utils/auth';
import { useCommunity } from '@/home/hooks/community';
import { setLanguageFromClient } from '@/common/utils/language';
import { useI18n } from 'vue-i18n';

const TOOLBAR_ITEM_HEIGHT = 64;
const atomgitUrl = import.meta.env.VITE_ATOMGIT_URL;

const menubarEl = ref(null);
const menubarItemsEl = ref(null);
const newMenuEl = ref(null);
const userMenuEl = ref(null);
const isForceMenubarClose = ref(false);

const appStore = useAppStore();
const { $fetch } = useFetch();
const route = useRoute();
const { isOutside: isMenubarOutside } = useMouseInElement(menubarEl);
const { height: menubarHeight } = useElementSize(menubarItemsEl);
const isNewMenuHovered = useElementHover(newMenuEl, { delayEnter: 200, delayLeave: 200 });
const isUserMenuHovered = useElementHover(userMenuEl, { delayEnter: 200, delayLeave: 200 });
const { t, locale } = useI18n();
const { name: communityName } = useCommunity();

const routerMenus = [
  {
    text: t('common.header.nav1'),
    to: '/',
  },
  {
    text: t('common.header.nav2'),
    to: '/explore',
  },
];

const { data: menubarItems } = useAsyncData('header:menubar', () => $fetch('/toolbar/queryToolbarListByCommunityId'));
const { data: unreadCount } = useAsyncData<Record<string, number>>('header:unread', () => appStore.user.id && $fetch('/user/unreadCount'));

// 临时这么写
const menuValue = computed(() => {
  if (route.path.indexOf('/explore') === 0) {
    return '/explore';
  }
  else {
    return '/';
  }
});

const expandable = computed(() => menubarHeight.value > TOOLBAR_ITEM_HEIGHT);
const expanded = computed(() => expandable.value && !isMenubarOutside.value && isForceMenubarClose.value !== true);
const isUnread = computed(() => Object.values(unreadCount.value ?? {}).reduce((x: number, y: number) => x + y, 0) > 0);

watch(isMenubarOutside, () => {
  isForceMenubarClose.value = false;
});

const login = () => {
  gotoLoginForClient();
}

const logout = () => {
  gotoLogoutForClient();
}

const languageValue = ref(locale.value === 'zh-CN');

const changeLanguage = ({ checked }: { checked: boolean }) => {
  setLanguageFromClient(checked ? 'zh-CN' : 'en-US');
  window.location.reload();
}

const hasShadow = computed(() => menuValue.value === '/');
</script>

<template>
  <header class="page-header relative z-9999 h-64 bg-white text-14 color-text-2 2xl:text-16" :class="{ 'shadow-[0_1px_6px_0_rgba(0,0,0,0.06)]': hasShadow }">
    <div class="mx-100 h-full flex-y-center">
      <div class="h-36 w-140 flex-shrink-0" v-if="appStore.config.logo">
        <RouterLink to="/" class="block size-full">
          <img :src="appStore.config.logo" :alt="appStore.config.name" class="block size-full object-contain object-left">
        </RouterLink>
      </div>

      <div class="relative mx-60 h-full flex flex-grow">
        <div class="absolute top-0 w-full flex justify-center" ref="menubarEl">
          <div class="min-w-0">
            <div
              class="overflow-hidden bg-white transition-max-height"
              :class="expanded ? 'max-h-100% rounded-b-4 shadow-[0_1px_6px_0_rgba(0,0,0,0.06)]' : 'max-h-64'"
            >
              <ul class="relative h-full flex flex-wrap gap-x-20 pb-15" ref="menubarItemsEl">
                <li class="h-64 flex items-center">
                  <SegmentGroup.Root v-model="menuValue" id="header-toolbar-navigation" orientation="horizontal" class="relative h-full flex items-center gap-x-20">
                    <SegmentGroup.Item as-child v-for="item in routerMenus" :key="item.to" :value="item.to">
                      <RouterLink class="mx-16 whitespace-nowrap no-underline hover:text-color-primary-2 focus-visible:outline-unset" :to="item.to">
                        {{ item.text }}
                      </RouterLink>
                    </SegmentGroup.Item>
                    <SegmentGroup.Indicator class="bottom-0 h-1 w-[var(--width)] bg-primary-2" />
                  </SegmentGroup.Root>
                </li>

                <li class="h-64 flex items-center" v-for="item in menubarItems" :key="item.barId">
                  <a :href="item.jumpUrl" target="_blank" class="whitespace-nowrap px-16 no-underline hover:text-color-primary-2 focus-visible:outline-unset">
                    {{ item.barName }}
                  </a>
                </li>

                <li class="absolute bottom-0 left-50% translate-x--50%" v-if="expandable">
                  <div class="h-12 w-32 flex-center cursor-pointer rounded-t-6 bg-gray-2 color-hex-8b8b8b" @click="isForceMenubarClose = true">
                    <div class="i-icons:arrow-down-1 h-8 w-8 rotate-180"></div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="absolute bottom-0 left-50% translate-x--50%" v-if="expandable && !expanded">
          <div class="h-12 w-32 flex-center rounded-t-6 bg-gray-2 color-hex-8b8b8b">
            <div class="i-icons:arrow-down-1 h-8 w-8"></div>
          </div>
        </div>
      </div>

      <div class="flex-y-center flex-shrink-0">
        <div class="flex-y-center gap-x-20 text-14" v-if="appStore.user.id">
          <a :href="`${atomgitUrl}/-/profile/notice/message`" target="_blank" class="relative block h-20 w-20" :aria-label="$t('common.header.notice.tooltip')">
            <div class="i-icons:notification h-full w-full"></div>
            <div class="absolute right--3 top--3 h-6 w-6 rounded-50% bg-hex-E24E40" v-if="isUnread"></div>
          </a>

          <div ref="newMenuEl">
            <Menu.Root id="page-header-menu-new" :open="isNewMenuHovered" lazy-mount close-on-select :positioning="{ placement: 'bottom-end', gutter: 0 }">
              <Menu.Trigger as-child>
                <button type="button" class="flex-x-center cursor-default gap-x-2 rounded-4 bg-primary-1 px-12 py-6 color-white leading-20 focus-visible:outline-unset">
                  <div class="i-icons:plus-1 h-20 w-20"></div>
                  {{ $t('common.header.new.menu1') }}
                </button>
              </Menu.Trigger>
              <Menu.Positioner>
                <div class="flex">
                  <div>
                    <Menu.Content as-child>
                      <div class="mt-12 overflow-hidden rounded-6 bg-white p-8 shadow-[0_5px_15px_0_rgba(0,0,0,0.15)] focus-visible:outline-unset">
                        <Menu.Item as-child value="repository">
                          <a :href="`${atomgitUrl}/project/new?community=${encodeURIComponent(communityName)}`" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2 focus-visible:outline-unset">
                            {{ $t('common.header.new.menu2') }}
                          </a>
                        </Menu.Item>
                        <!-- <Menu.Item as-child value="import">
                          <a :href="`${atomgitUrl}/project/new?community=${encodeURIComponent(communityName)}`" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            {{ $t('common.header.new.menu3') }}
                          </a>
                        </Menu.Item> -->
                        <Menu.Item as-child value="organization">
                          <a :href="`${atomgitUrl}/dashboard/orgs/create?community=${encodeURIComponent(communityName)}`" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2 focus-visible:outline-unset">
                            {{ $t('common.header.new.menu4') }}
                          </a>
                        </Menu.Item>
                      </div>
                    </Menu.Content>
                  </div>
                </div>
              </Menu.Positioner>
            </Menu.Root>
          </div>

          <div ref="userMenuEl">
            <Menu.Root id="page-header-menu-user" :open="isUserMenuHovered" lazy-mount close-on-select :positioning="{ placement: 'bottom-end', gutter: 0 }">
              <Menu.Trigger as-child>
                <div class="h-24 w-24 focus-visible:outline-unset">
                  <img :src="appStore.user.photo" class="block h-full w-full rounded-50% object-cover" :alt="appStore.user.username">
                </div>
              </Menu.Trigger>
              <Menu.Positioner>
                <div class="flex">
                  <div>
                    <Menu.Content as-child>
                      <div class="mt-17 overflow-hidden rounded-6 bg-white p-8 shadow-[0_5px_15px_0_rgba(0,0,0,0.15)] focus-visible:outline-unset">
                        <Menu.Item as-child value="user-info" :close-on-select="false">
                          <div class="px-20 pb-4">
                            <p class="font-medium leading-20">{{ appStore.user.nickname }}</p>
                            <p class="text-12 leading-20">@{{ appStore.user.username }}</p>
                          </div>
                        </Menu.Item>
                        <Menu.Separator as-child>
                          <hr class="my-8 b-color-gray-2">
                        </Menu.Separator>
                        <Menu.Item as-child value="language" :close-on-select="false">
                          <div class="h-44 flex-y-center gap-x-6 px-20 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            <div>{{ $t('common.languageName', '', { locale: 'zh-CN' }) }}</div>
                            <Switch.Root v-model:checked="languageValue" id="page-header-switch-language" class="mt-2 block" @checked-change="changeLanguage">
                              <Switch.Control class="flex-shark-0 block h-16 w-28 flex-y-center cursor-pointer rounded-full bg-gray-3 p-2 transition-background-color data-[state=checked]:bg-primary-2">
                                <Switch.Thumb class="block h-12 w-12 translate-x-100% rounded-full bg-white transition-transform data-[state=checked]:translate-x-0" />
                              </Switch.Control>
                              <Switch.HiddenInput as-child>
                                <input type="checkbox">
                              </Switch.HiddenInput>
                            </Switch.Root>
                            <div>{{ $t('common.languageName', '', { locale: 'en-US' }) }}</div>
                          </div>
                        </Menu.Item>
                        <Menu.Item as-child value="my-homepage">
                          <a :href="`${atomgitUrl}/${appStore.user.username}`" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            {{ $t('common.header.user.menu1') }}
                          </a>
                        </Menu.Item>
                        <Menu.Item as-child value="my-repository">
                          <a :href="`${atomgitUrl}/users/${appStore.user.username}/projects`" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            {{ $t('common.header.user.menu2') }}
                          </a>
                        </Menu.Item>
                        <Menu.Item as-child value="my-organization">
                          <a :href="`${atomgitUrl}/-/profile/orgs`" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            {{ $t('common.header.user.menu3') }}
                          </a>
                        </Menu.Item>
                        <Menu.Item as-child value="my-changes">
                          <a :href="`${atomgitUrl}/dashboard/changes`" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            {{ $t('common.header.user.menu4') }}
                          </a>
                        </Menu.Item>
                        <Menu.Item as-child value="my-issues">
                          <a :href="`${atomgitUrl}/dashboard/issues`" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            {{ $t('common.header.user.menu5') }}
                          </a>
                        </Menu.Item>
                        <Menu.Item as-child value="my-starred">
                          <a :href="`${atomgitUrl}/users/${appStore.user.username}/starred`" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            {{ $t('common.header.user.menu6') }}
                          </a>
                        </Menu.Item>
                        <Menu.Item as-child value="my-profile">
                          <a :href="`${atomgitUrl}/-/profile`" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            {{ $t('common.header.user.menu7') }}
                          </a>
                        </Menu.Item>
                        <Menu.Item as-child value="my-settings" v-if="appStore.user.isAdmin">
                          <a href="/settings" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            {{ $t('common.header.user.menu8') }}
                          </a>
                        </Menu.Item>
                        <Menu.Separator as-child>
                          <hr class="my-8 b-color-gray-2">
                        </Menu.Separator>
                        <Menu.Item as-child value="help">
                          <a href="https://docs.atomgit.com/" target="_blank" class="block px-20 leading-44 data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2">
                            {{ $t('common.header.user.menu9') }}
                          </a>
                        </Menu.Item>
                        <Menu.Item as-child value="logout">
                          <button type="button" class="block h-44 w-full bg-transparent px-20 text-left data-[highlighted]:rounded-4 data-[highlighted]:bg-gray-2" @click="logout">
                            {{ $t('common.header.user.menu10') }}
                          </button>
                        </Menu.Item>
                      </div>
                    </Menu.Content>
                  </div>
                </div>
              </Menu.Positioner>
            </Menu.Root>
          </div>
        </div>

        <div class="flex gap-x-16 text-14" v-else>
          <button type="button" class="bg-transparent px-12 py-6 hover:color-primary-2" @click="login">{{ $t('common.header.register') }}</button>
          <button type="button" class="rounded-4 bg-primary-1 px-12 py-6 color-white" @click="login">{{ $t('common.header.login') }}</button>
        </div>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.page-header {
  [data-scope=menu][data-part=content][hidden] {
    display: none;
  }

  [data-scope=menu][data-part=content][data-state=open] {
    animation: fadeIn 0.15s ease-out;
  }

  [data-scope=menu][data-part=content][data-state=closed] {
    animation: fadeOut 0.1s ease-out;
  }

  @keyframes fadeIn {
    from {
      max-height: 0;
      opacity: 0;
    }

    to {
      max-height: 100%;
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    from {
      max-height: 100%;
      opacity: 1;
    }

    to {
      max-height: 0;
      opacity: 0;
    }
  }
}
</style>
